import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Bell } from 'lucide-react';
import { doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { syncFcmRegistration } from '@/lib/messaging';
import { useAuthStore } from '@/stores/authStore';
import { useToastStore } from '@/stores/toastStore';
import { Card, CardContent, Button, Checkbox, Loading } from '@/components/ui';
import type { NotificationSettings } from '@kulum/shared';

const SETTINGS_DOC_ID = 'preferences';

const DEFAULT_NOTIFICATION_SETTINGS: NotificationSettings = {
  email: {
    messages: true,
    profileRequests: true,
    reviews: true,
    marketing: false,
  },
  push: {
    messages: true,
    profileRequests: true,
    reviews: true,
  },
};

function normalizeNotificationSettings(raw: unknown): NotificationSettings {
  if (typeof raw !== 'object' || raw === null) {
    return DEFAULT_NOTIFICATION_SETTINGS;
  }

  const rawSettings = raw as {
    email?: Partial<NotificationSettings['email']>;
    push?: Partial<NotificationSettings['push']>;
  };

  return {
    email: {
      messages: typeof rawSettings.email?.messages === 'boolean'
        ? rawSettings.email.messages
        : DEFAULT_NOTIFICATION_SETTINGS.email.messages,
      profileRequests: typeof rawSettings.email?.profileRequests === 'boolean'
        ? rawSettings.email.profileRequests
        : DEFAULT_NOTIFICATION_SETTINGS.email.profileRequests,
      reviews: typeof rawSettings.email?.reviews === 'boolean'
        ? rawSettings.email.reviews
        : DEFAULT_NOTIFICATION_SETTINGS.email.reviews,
      marketing: typeof rawSettings.email?.marketing === 'boolean'
        ? rawSettings.email.marketing
        : DEFAULT_NOTIFICATION_SETTINGS.email.marketing,
    },
    push: {
      messages: typeof rawSettings.push?.messages === 'boolean'
        ? rawSettings.push.messages
        : DEFAULT_NOTIFICATION_SETTINGS.push.messages,
      profileRequests: typeof rawSettings.push?.profileRequests === 'boolean'
        ? rawSettings.push.profileRequests
        : DEFAULT_NOTIFICATION_SETTINGS.push.profileRequests,
      reviews: typeof rawSettings.push?.reviews === 'boolean'
        ? rawSettings.push.reviews
        : DEFAULT_NOTIFICATION_SETTINGS.push.reviews,
    },
  };
}

export function NotificationSettingsPage() {
  const user = useAuthStore((state) => state.user);
  const addToast = useToastStore((state) => state.addToast);

  const [settings, setSettings] = useState<NotificationSettings>(DEFAULT_NOTIFICATION_SETTINGS);
  const [initialSettings, setInitialSettings] = useState<NotificationSettings>(DEFAULT_NOTIFICATION_SETTINGS);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const loadSettings = async () => {
      if (!user?.uid) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const settingsRef = doc(db, 'users', user.uid, 'settings', SETTINGS_DOC_ID);
        const settingsSnap = await getDoc(settingsRef);

        if (!settingsSnap.exists()) {
          await setDoc(settingsRef, {
            notifications: DEFAULT_NOTIFICATION_SETTINGS,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          });
          setSettings(DEFAULT_NOTIFICATION_SETTINGS);
          setInitialSettings(DEFAULT_NOTIFICATION_SETTINGS);
          return;
        }

        const data = settingsSnap.data() as { notifications?: unknown };
        const normalized = normalizeNotificationSettings(data.notifications);
        setSettings(normalized);
        setInitialSettings(normalized);
      } catch (error) {
        console.error('Error loading notification settings:', error);
        addToast('error', '通知設定の読み込みに失敗しました');
      } finally {
        setIsLoading(false);
      }
    };

    void loadSettings();
  }, [user?.uid, addToast]);

  const isDirty = useMemo(
    () => JSON.stringify(settings) !== JSON.stringify(initialSettings),
    [settings, initialSettings]
  );
  const pushEnabled = useMemo(
    () => Object.values(settings.push).some(Boolean),
    [settings.push]
  );

  const updateEmailSetting = (key: keyof NotificationSettings['email'], checked: boolean) => {
    setSettings((prev) => ({
      ...prev,
      email: {
        ...prev.email,
        [key]: checked,
      },
    }));
  };

  const updatePushSetting = (key: keyof NotificationSettings['push'], checked: boolean) => {
    setSettings((prev) => ({
      ...prev,
      push: {
        ...prev.push,
        [key]: checked,
      },
    }));
  };

  const handleSave = async () => {
    if (!user?.uid) return;

    try {
      setIsSaving(true);
      if (isDirty) {
        const settingsRef = doc(db, 'users', user.uid, 'settings', SETTINGS_DOC_ID);
        await setDoc(
          settingsRef,
          {
            notifications: settings,
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
      }

      const fcmStatus = await syncFcmRegistration({
        userId: user.uid,
        pushEnabled,
        allowPermissionPrompt: pushEnabled,
      });

      if (isDirty) {
        setInitialSettings(settings);
        addToast('success', '通知設定を保存しました');
      }

      if (pushEnabled) {
        switch (fcmStatus) {
          case 'registered':
            if (!isDirty) {
              addToast('success', 'プッシュ通知を有効化しました');
            }
            break;
          case 'permission_denied':
            addToast('warning', 'ブラウザでプッシュ通知が拒否されています。ブラウザ設定で許可してください。');
            break;
          case 'permission_default':
            addToast('info', 'プッシュ通知の許可が未設定です。必要に応じてブラウザで許可してください。');
            break;
          case 'unsupported':
            addToast('warning', 'このブラウザではプッシュ通知に対応していません。');
            break;
          case 'missing_vapid_key':
            addToast('error', 'VAPIDキーが未設定のため、プッシュ通知を有効化できません。');
            break;
          case 'error':
            addToast('error', 'プッシュ通知の設定反映に失敗しました。');
            break;
          default:
            break;
        }
      }
    } catch (error) {
      console.error('Error saving notification settings:', error);
      addToast('error', '通知設定の保存に失敗しました');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-6">
        <Loading message="通知設定を読み込み中..." />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      <Link
        to="/mypage"
        className="inline-flex items-center gap-2 text-neutral-600 hover:text-copper mb-6 transition"
      >
        <ArrowLeft className="w-4 h-4" />
        <span className="text-sm">マイページに戻る</span>
      </Link>

      <h1 className="text-2xl font-heading-luxury text-luxury-dark mb-2">
        通知設定
      </h1>
      <p className="text-neutral-600 mb-6">
        受け取りたい通知チャネルを選択できます。
      </p>

      <div className="space-y-4">
        <Card>
          <CardContent className="py-5">
            <div className="flex items-center gap-2 mb-4">
              <Bell className="w-5 h-5 text-copper" />
              <h2 className="font-bold text-luxury-dark">メール通知</h2>
            </div>
            <div className="space-y-3">
              <Checkbox
                label="メッセージ通知"
                checked={settings.email.messages}
                onChange={(e) => updateEmailSetting('messages', e.target.checked)}
              />
              <Checkbox
                label="閲覧申請通知"
                checked={settings.email.profileRequests}
                onChange={(e) => updateEmailSetting('profileRequests', e.target.checked)}
              />
              <Checkbox
                label="レビュー通知"
                checked={settings.email.reviews}
                onChange={(e) => updateEmailSetting('reviews', e.target.checked)}
              />
              <Checkbox
                label="キャンペーン・お知らせ"
                checked={settings.email.marketing}
                onChange={(e) => updateEmailSetting('marketing', e.target.checked)}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="py-5">
            <div className="flex items-center gap-2 mb-4">
              <Bell className="w-5 h-5 text-copper" />
              <h2 className="font-bold text-luxury-dark">プッシュ通知</h2>
            </div>
            <div className="space-y-3">
              <Checkbox
                label="メッセージ通知"
                checked={settings.push.messages}
                onChange={(e) => updatePushSetting('messages', e.target.checked)}
              />
              <Checkbox
                label="閲覧申請通知"
                checked={settings.push.profileRequests}
                onChange={(e) => updatePushSetting('profileRequests', e.target.checked)}
              />
              <Checkbox
                label="レビュー通知"
                checked={settings.push.reviews}
                onChange={(e) => updatePushSetting('reviews', e.target.checked)}
              />
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="mt-6 flex justify-end">
        <Button
          onClick={handleSave}
          isLoading={isSaving}
          disabled={isSaving}
        >
          {isDirty ? '保存する' : 'プッシュ通知を確認する'}
        </Button>
      </div>
    </div>
  );
}
